import React from 'react';
import AdminPublicLayout from "@/layouts/AdminPublicLayout";
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { Helmet } from "react-helmet";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

const AffiliateProgram = () => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const formSchema = z.object({
        firstName: z.string().min(1, t('affiliate.firstNameMin') || "First name is required"),
        lastName: z.string().min(1, t('affiliate.lastNameMin') || "Last name is required"),
        email: z.string().email(t('affiliate.emailInvalid') || "Invalid email address"),
    });

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
        },
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            const response = await fetch(`${import.meta.env.VITE_PUBLIC_AFFILIATE_REQUEST_ENDPOINT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(values),
            });

            if (!response.ok) throw new Error("Failed to register");

            toast({
                title: t("contactUs.requestSent"),
                description: t("contactUs.weWillGetBack"),
                className: "bg-green-500 text-white border-green-600",
                duration: 5000,
            });

            form.reset();
        } catch (error) {
            console.error('Form submission error:', error);
            toast({
                title: t("contactUs.toastErrorTitle"),
                description: t("contactUs.toastErrorDescription"),
                variant: "destructive",
                duration: 5000,
            });
        }
    };

    return (
        <AdminPublicLayout>
            <Helmet>
                <title>Join FitCalendar Affiliate Program - Earn €96 Per Referral</title>
                <link rel="canonical" href="https://fitcalendar.praxipro.io/affiliate-program/" />
                <meta name="title" content="Join FitCalendar Affiliate Program - Earn €96 Per Referral" />
                <meta name="description"
                    content="Join the FitCalendar affiliate program and earn €96/year or €8/month per referral. Invite yoga, pilates, or fitness studios to our booking software." />
                <meta name="author" content="PraxiPro" />
                {/* Open Graph tags for social sharing */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://fitcalendar.praxipro.io/affiliate-program/" />
                <meta property="og:title" content="Join FitCalendar Affiliate Program - Earn €96 Per Referral" />
                <meta property="og:description"
                    content="Partner with FitCalendar and earn commissions by referring yoga, pilates, and fitness studios. €96 per yearly subscription plus lifetime bonuses available." />
                <meta property="og:image" content="/og-image.png" />
                <meta property="og:site_name" content="FitCalendar Affiliate Program" />
                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://fitcalendar.praxipro.io/affiliate-program/" />
                <meta name="twitter:title" content="Join FitCalendar Affiliate Program - Earn €96 Per Referral" />
                <meta name="twitter:description"
                    content="Earn money by referring yoga, pilates, and fitness studios to FitCalendar. €96 per yearly subscription, €8/month for monthly plans." />
                <meta name="twitter:image" content="/og-image.png" />
                <meta name="twitter:creator" content="@PraxiPro" />
                {/* Schema.org microdata */}
                <meta itemProp="name" content="Join FitCalendar Affiliate Program - Earn €96 Per Referral" />
                <meta itemProp="description"
                    content="Partner with FitCalendar's affiliate program. Earn €96 for yearly subscriptions, €8/month for monthly plans, plus lifetime bonuses for top performers." />
                <meta itemProp="image" content="/og-image.png" />
                
                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="flex-1 py-16 px-4">
                <section className="text-center max-w-3xl mx-auto">
                    <h1 className="text-4xl font-bold mb-4">{t("affiliate.heading")}</h1>
                    <p className="text-lg text-muted-foreground mb-6">
                        {t("affiliate.description")}
                    </p>
                    <div className="bg-primary/5 p-6 rounded-lg shadow mb-10">
                        <ul className="text-left space-y-4">
                            <li><strong>{t("affiliate.rewardOne.amount")}</strong> {t("affiliate.rewardOne.text")}</li>
                            <li><strong>{t("affiliate.rewardTwo.amount")}</strong> {t("affiliate.rewardTwo.text")}</li>
                            <li><strong>{t("affiliate.rewardThree.title")}</strong> {t("affiliate.rewardThree.description")} <strong>{t("affiliate.rewardThree.amount")}</strong> — <em>{t("affiliate.rewardThree.forever")}</em>.</li>
                        </ul>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("affiliate.ctaHeading")}</h2>
                    <p className="mb-6">{t("affiliate.ctaDescription")}</p>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 max-w-md mx-auto">
                            <div className="flex flex-col md:flex-row md:space-x-4">
                                <FormField
                                    control={form.control}
                                    name="firstName"
                                    render={({ field }) => (
                                        <FormItem className="flex-1">
                                            <FormControl>
                                                <Input placeholder={t("affiliate.form.firstName")} {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="lastName"
                                    render={({ field }) => (
                                        <FormItem className="flex-1 mt-4 md:mt-0">
                                            <FormControl>
                                                <Input placeholder={t("affiliate.form.lastName")} {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input placeholder={t("affiliate.form.email")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                                {form.formState.isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("affiliate.form.loading")}
                                    </>
                                ) : (
                                    <>{t("affiliate.form.submit")}</>
                                )}
                            </Button>
                        </form>
                    </Form>
                </section>
            </main>
        </AdminPublicLayout>
    );
};

export default AffiliateProgram;
